import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store.ts";
import { FetchAPIData } from "../../utils/GlobalFunctions/fetch.ts";

export type MediaReviewsSectionProps = {
  mediaType: "movie" | "tv";
  mediaId: string | undefined;
};

type ReviewAuthorDetails = {
  username?: string;
  avatar_path?: string | null;
  rating?: number | null;
};

type ReviewResult = {
  id?: string;
  author?: string;
  author_details?: ReviewAuthorDetails;
  content?: string;
  created_at?: string;
  url?: string;
};

type ReviewsApiResponse = {
  success?: boolean;
  status_message?: string;
  results?: ReviewResult[];
  page?: number;
  total_pages?: number;
  total_results?: number;
};

function formatReviewDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function avatarSrc(path: string | null | undefined): string | null {
  if (!path || !String(path).length) return null;
  const p = String(path);
  if (p.startsWith("/http")) {
    return p.slice(1);
  }
  return `https://image.tmdb.org/t/p/w45${p}`;
}

const MediaReviewsSection: React.FC<MediaReviewsSectionProps> = ({
  mediaType,
  mediaId,
}) => {
  const sessionData = useSelector((state: RootState) => state.session);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<ReviewResult[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setTotalPages(0);
    setTotalResults(0);
    setError(null);
    setExpanded({});
  }, [mediaId, mediaType]);

  const loadPage = useCallback(
    async (nextPage: number) => {
      if (!mediaId) return;
      setLoading(true);
      setError(null);
      try {
        const data: ReviewsApiResponse = await FetchAPIData(
          `${mediaType}/${mediaId}/reviews`,
          sessionData,
          { page: String(nextPage) },
        );
        if (data.success === false) {
          setError(data.status_message || "Could not load reviews.");
          return;
        }
        const batch = data.results || [];
        setTotalPages(data.total_pages ?? 0);
        setTotalResults(data.total_results ?? 0);
        setAccumulated((prev) =>
          nextPage === 1 ? batch : [...prev, ...batch],
        );
        setPage(nextPage);
      } catch {
        setError("Could not load reviews.");
      } finally {
        setLoading(false);
      }
    },
    [mediaId, mediaType, sessionData],
  );

  useEffect(() => {
    if (!mediaId) return;
    void loadPage(1);
  }, [mediaId, mediaType, loadPage]);

  const canLoadMore =
    totalPages > 0 && page < totalPages && !loading && !error;

  if (!mediaId) {
    return null;
  }

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="media-reviews-section">
      <h3 className="media-reviews-heading">User reviews</h3>
      {error && (
        <p className="media-reviews-error" role="alert">
          {error}
        </p>
      )}
      {!error && !loading && accumulated.length === 0 && (
        <p className="media-reviews-empty text-muted">
          No written reviews on TMDB yet.
        </p>
      )}
      <ul className="media-reviews-list">
        {accumulated.map((rev, idx) => {
          const key = `${rev.id ?? rev.author ?? "r"}-${idx}`;
          const isOpen = Boolean(expanded[key]);
          const content = rev.content?.trim() || "";
          const long = content.length > 320;
          const displayContent =
            long && !isOpen ? `${content.slice(0, 320)}…` : content;

          return (
            <li key={key} className="media-reviews-item">
              <div className="media-reviews-item-head">
                <div className="media-reviews-avatar-wrap">
                  {avatarSrc(rev.author_details?.avatar_path) ? (
                    <img
                      src={avatarSrc(rev.author_details?.avatar_path) || ""}
                      alt=""
                      className="media-reviews-avatar"
                      width={45}
                      height={45}
                    />
                  ) : (
                    <div
                      className="media-reviews-avatar media-reviews-avatar-fallback"
                      aria-hidden
                    />
                  )}
                </div>
                <div className="media-reviews-meta">
                  <span className="media-reviews-author">
                    {rev.author || "Anonymous"}
                  </span>
                  {rev.author_details?.rating != null && (
                    <span className="media-reviews-rating text-muted">
                      Rating: {rev.author_details.rating}/10
                    </span>
                  )}
                  {rev.created_at && (
                    <span className="media-reviews-date text-muted">
                      {formatReviewDate(rev.created_at)}
                    </span>
                  )}
                </div>
              </div>
              {content ? (
                <div className="media-reviews-body">
                  <p className="media-reviews-content">{displayContent}</p>
                  {long && (
                    <button
                      type="button"
                      className="media-reviews-toggle"
                      onClick={() => toggleExpand(key)}
                    >
                      {isOpen ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              ) : null}
              {rev.url && (
                <a
                  href={rev.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="media-reviews-external"
                >
                  View on TMDB
                </a>
              )}
            </li>
          );
        })}
      </ul>
      {loading && accumulated.length === 0 && (
        <p className="media-reviews-loading text-muted">Loading reviews…</p>
      )}
      {loading && accumulated.length > 0 && (
        <p className="media-reviews-loading text-muted">Loading more…</p>
      )}
      {canLoadMore && (
        <button
          type="button"
          className="btn media-reviews-more"
          onClick={() => void loadPage(page + 1)}
          disabled={loading}
        >
          Load more reviews
        </button>
      )}
      {totalResults > 0 && (
        <p className="media-reviews-footer text-muted">
          Showing {accumulated.length} of {totalResults} on TMDB
        </p>
      )}
    </div>
  );
};

export default MediaReviewsSection;
